import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projects, invoices, type Project, type Milestone } from '../lib/api';
import { queryKeys } from './queryKeys';

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useProjects(params?: { client_id?: number; status?: string }) {
  return useQuery({
    queryKey: queryKeys.projects.list(params as Record<string, unknown>),
    queryFn: () => projects.list(params),
  });
}

export function useProject(id: number) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => projects.get(id),
    enabled: id > 0,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { client_id: number; title: string; description?: string; total_amount: number; upfront_percent?: number; proposal_id?: number }) =>
      projects.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.projects.all }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Pick<Project, 'title' | 'description' | 'status' | 'total_amount' | 'upfront_percent' | 'count_in_stats'>> }) =>
      projects.update(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

export function useAddMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: number; data: { title: string; description?: string; amount: number; percentage?: number; due_date?: string } }) =>
      projects.addMilestone(projectId, data),
    onSuccess: (_d, { projectId }) => qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
  });
}

export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, milestoneId, data }: { projectId: number; milestoneId: number; data: Partial<Pick<Milestone, 'title' | 'description' | 'amount' | 'percentage' | 'status' | 'due_date'>> }) =>
      projects.updateMilestone(projectId, milestoneId, data),
    onSuccess: (_d, { projectId }) => qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
  });
}

export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, milestoneId }: { projectId: number; milestoneId: number }) =>
      projects.deleteMilestone(projectId, milestoneId),
    onSuccess: (_d, { projectId }) => qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
  });
}

export function useAddTimelineEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: number; data: { event_type: string; title: string; description?: string; is_client_visible?: boolean; milestone_id?: number } }) =>
      projects.addTimelineEvent(projectId, data),
    onSuccess: (_d, { projectId }) => qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId) }),
  });
}

export function useInviteClient() {
  return useMutation({
    mutationFn: (clientId: number) => projects.inviteClient(clientId),
  });
}

export function useGenerateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: number) => invoices.generate(paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      qc.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}
